import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../store/hooks';
import { fetchKudirStats, KudirStats } from '../store/slices/accountingSlice';
import LoadingSpinner from './LoadingSpinner';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  LabelList,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
} from 'recharts';
import { buildCompareData, formatDeltaPct, yearOffset, CompareRow } from './accountingStatsCompare';
import './Accounting.css';

type Axis = 'service' | 'payment';
type GroupBy = 'month' | 'quarter' | 'year';
type Metric = 'revenue' | 'tax' | 'count';
// 'values' = the plain per-period chart; 'prev' = each period's change against its neighbour
// (one bar per period); 'yoy' = each period next to the same period of the previous year.
type View = 'values' | 'prev' | 'yoy';

// App-standard number formatting: space thousands separators, dot decimal (matches Accounting.tsx).
const fmt = (n: number): string => n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
const fmtInt = (n: number): string => Math.round(n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

const METRIC_LABEL: Record<Metric, string> = { revenue: 'Выручка', tax: 'Налог 20%', count: 'Актов' };

// Revenue bars split into two stacked blocks so the tax slice is visible inside each period:
// the amount kept after tax (bottom) + the КУДИР tax owed (top) = the gross revenue (whole bar).
const COLOR_KEPT = '#667eea'; // revenue − tax (kept after tax)
const COLOR_TAX = '#ed8936';  // налог 20 %
const LABEL_STYLE = { fill: '#334155', fontSize: 11, fontWeight: 600 };
const COLOR_PREV = '#cbd5e1'; // same period a year earlier (year-over-year view)
const COLOR_UP = '#15803d';
const COLOR_DOWN = '#b91c1c';
// Above this many bars the value labels are hidden to avoid clutter (quarter/year always show).
const LABEL_LIMIT = 24;

function monthLabel(bucket: string): string {
  const [y, m] = bucket.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
}

interface ChartRow { bucket: string; label: string; revenue: number; netIncome: number; tax: number; afterTax: number; count: number }

// Turn the API series into a continuous timeline, filling gaps (months/years with no revenue) with 0
// so the trend reads correctly instead of collapsing empty periods.
function buildChartData(stats: KudirStats | null): ChartRow[] {
  if (!stats || stats.series.length === 0) return [];
  const byBucket = new Map(stats.series.map((p) => [p.bucket, p]));
  const first = stats.series[0].bucket;
  const last = stats.series[stats.series.length - 1].bucket;

  const keys: string[] = [];
  if (stats.groupBy === 'year') {
    for (let y = Number(first); y <= Number(last); y += 1) keys.push(String(y));
  } else if (stats.groupBy === 'quarter') {
    const idx = (b: string) => { const [y, q] = b.split('-Q').map(Number); return y * 4 + (q - 1); };
    for (let i = idx(first); i <= idx(last); i += 1) keys.push(`${Math.floor(i / 4)}-Q${(i % 4) + 1}`);
  } else {
    let [y, m] = first.split('-').map(Number);
    const [ly, lm] = last.split('-').map(Number);
    while (y < ly || (y === ly && m <= lm)) {
      keys.push(`${y}-${String(m).padStart(2, '0')}`);
      m += 1;
      if (m > 12) { m = 1; y += 1; }
    }
  }

  const label = (k: string) =>
    stats.groupBy === 'year' ? k : stats.groupBy === 'quarter' ? k.replace('-Q', ' Q') : monthLabel(k);

  return keys.map((k) => {
    const p = byBucket.get(k);
    return {
      bucket: k,
      label: label(k),
      revenue: p?.revenue ?? 0,
      netIncome: p?.netIncome ?? 0,
      tax: p?.tax ?? 0,
      afterTax: (p?.revenue ?? 0) - (p?.tax ?? 0),
      count: p?.count ?? 0,
    };
  });
}

interface StatsTooltipProps {
  active?: boolean;
  label?: string;
  payload?: Array<{ payload: ChartRow }>;
}

// Regardless of the plotted metric, the tooltip shows the full КУДИР breakdown for the period.
function StatsTooltip({ active, label, payload }: StatsTooltipProps) {
  if (!active || !payload || payload.length === 0) return null;
  const row = payload[0].payload;
  return (
    <div className="accounting-stats-tooltip">
      <div className="accounting-stats-tooltip__label">{label}</div>
      <div className="accounting-stats-tooltip__row"><em>Выручка</em> {fmt(row.revenue)} BYN</div>
      <div className="accounting-stats-tooltip__row"><em>Доход 80%</em> {fmt(row.netIncome)} BYN</div>
      <div className="accounting-stats-tooltip__row accounting-stats-tooltip__row--tax"><em>Налог 20%</em> {fmt(row.tax)} BYN</div>
      <div className="accounting-stats-tooltip__row"><em>Актов</em> {row.count}</div>
    </div>
  );
}

interface CompareTooltipProps {
  active?: boolean;
  label?: string;
  payload?: Array<{ payload: CompareRow }>;
  metric: Metric;
  emptyLabel: string;
}

// Comparison views: the period's value, the compared period's value and the change between them.
function CompareTooltip({ active, label, payload, metric, emptyLabel }: CompareTooltipProps) {
  if (!active || !payload || payload.length === 0) return null;
  const row = payload[0].payload;
  const unit = metric === 'count' ? '' : ' BYN';
  const sign = row.delta !== null && row.delta > 0 ? '+' : row.delta !== null && row.delta < 0 ? '−' : '';
  const pct = formatDeltaPct(row.deltaPct);
  return (
    <div className="accounting-stats-tooltip">
      <div className="accounting-stats-tooltip__label">{label} · {METRIC_LABEL[metric]}</div>
      <div className="accounting-stats-tooltip__row"><em>{label}</em> {fmt(row.current)}{unit}</div>
      {row.prev === null || row.delta === null ? (
        <div className="accounting-stats-tooltip__row"><em>{emptyLabel}</em> —</div>
      ) : (
        <>
          <div className="accounting-stats-tooltip__row"><em>{row.prevLabel}</em> {fmt(row.prev)}{unit}</div>
          <div className={`accounting-stats-tooltip__row accounting-stats-tooltip__row--${row.delta < 0 ? 'down' : 'up'}`}>
            <em>Изменение</em> {sign}{fmt(Math.abs(row.delta))}{unit}{pct && ` (${pct})`}
          </div>
        </>
      )}
    </div>
  );
}

function Segmented<T extends string>({ label, value, options, onChange }: {
  label: string;
  value: T;
  options: Array<{ value: T; label: string }>;
  onChange: (v: T) => void;
}) {
  return (
    <div className="accounting-segments" role="group" aria-label={label}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          className={`accounting-segments__btn ${value === o.value ? 'is-active' : ''}`}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function AccountingStats() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { kudirStats, kudirStatsLoading, error } = useAppSelector((state) => state.accounting);

  const [axis, setAxis] = useState<Axis>('service');
  const [groupBy, setGroupBy] = useState<GroupBy>('quarter');
  const [metric, setMetric] = useState<Metric>('revenue');
  const [view, setView] = useState<View>('values');

  useEffect(() => {
    dispatch(fetchKudirStats({ axis, groupBy }));
  }, [dispatch, axis, groupBy]);

  const data = useMemo(() => buildChartData(kudirStats), [kudirStats]);
  const grandTotal = useMemo(() => data.reduce((s, r) => s + r.revenue, 0), [data]);
  const totalActs = useMemo(() => data.reduce((s, r) => s + r.count, 0), [data]);
  // Grouped by year, the previous year IS the neighbouring period, so the year-over-year view
  // would only repeat the «К предыдущему» one — it is not offered there.
  const activeView: View = view === 'yoy' && groupBy === 'year' ? 'prev' : view;
  const compareData = useMemo(
    () => buildCompareData(data, (r) => r[metric], activeView === 'yoy' ? yearOffset(groupBy) : 1),
    [data, metric, activeView, groupBy]
  );

  return (
    <div className="students-container">
      <div className="page-header">
        <div className="page-header-content">
          <h1>Статистика КУДИР</h1>
          <p>Выручка по периодам — по дате услуги или дате платежа</p>
        </div>
        <Segmented
          label="Раздел"
          value="stats"
          options={[{ value: 'ledger', label: 'Реестр' }, { value: 'stats', label: 'Статистика' }]}
          onChange={(v) => { if (v === 'ledger') navigate('/accounting'); }}
        />
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="filter-bar">
        <Segmented<GroupBy>
          label="Группировка"
          value={groupBy}
          options={[
            { value: 'month', label: 'Месяц' },
            { value: 'quarter', label: 'Квартал' },
            { value: 'year', label: 'Год' },
          ]}
          onChange={setGroupBy}
        />
        <Segmented<Axis>
          label="Ось дат"
          value={axis}
          options={[{ value: 'service', label: 'Дата услуги' }, { value: 'payment', label: 'Дата платежа' }]}
          onChange={setAxis}
        />
        <span className="filter-sep filter-sep--divider" aria-hidden="true" />
        <Segmented<Metric>
          label="Показатель"
          value={metric}
          options={[
            { value: 'revenue', label: 'Выручка' },
            { value: 'tax', label: 'Налог 20%' },
            { value: 'count', label: 'Актов' },
          ]}
          onChange={setMetric}
        />
        <span className="filter-sep filter-sep--divider" aria-hidden="true" />
        <Segmented<View>
          label="Вид"
          value={activeView}
          options={[
            { value: 'values', label: 'Значения' },
            { value: 'prev', label: 'К предыдущему' },
            ...(groupBy === 'year' ? [] : [{ value: 'yoy' as View, label: 'Год к году' }]),
          ]}
          onChange={setView}
        />
      </div>

      <div className="accounting-kudir-totals" data-testid="stats-totals">
        <span className="accounting-kudir-totals__scope">КУДИР · вся история</span>
        <span className="accounting-kudir-totals__item"><em>Выручка</em> {fmt(grandTotal)} BYN</span>
        <span className="accounting-kudir-totals__item"><em>Актов</em> {totalActs}</span>
        {activeView === 'yoy' && (
          <span className="accounting-stats-legend">
            <span className="accounting-stats-legend__item"><i style={{ background: COLOR_PREV }} />Год назад</span>
            <span className="accounting-stats-legend__item">
              <i style={{ background: metric === 'tax' ? COLOR_TAX : COLOR_KEPT }} />{METRIC_LABEL[metric]}
            </span>
          </span>
        )}
        {activeView === 'values' && metric === 'revenue' && (
          <span className="accounting-stats-legend">
            <span className="accounting-stats-legend__item"><i style={{ background: COLOR_KEPT }} />После налога</span>
            <span className="accounting-stats-legend__item"><i style={{ background: COLOR_TAX }} />Налог 20%</span>
          </span>
        )}
      </div>

      {kudirStatsLoading ? (
        <div className="accounting-stats-chart accounting-stats-chart--loading"><LoadingSpinner /></div>
      ) : data.length === 0 ? (
        <div className="accounting-stats-chart accounting-stats-chart--empty">Нет данных</div>
      ) : activeView !== 'values' ? (
        <div className="accounting-stats-chart" data-testid="stats-compare-chart">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={compareData} margin={{ top: 24, right: 12, left: 8, bottom: 56 }} barGap={2}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 11 }}
                angle={groupBy === 'year' ? 0 : -32}
                textAnchor={groupBy === 'year' ? 'middle' : 'end'}
                height={52}
                interval={0}
              />
              <YAxis tick={{ fontSize: 12 }} width={64} tickFormatter={(v) => fmtInt(Number(v))} />
              <Tooltip
                content={<CompareTooltip metric={metric} emptyLabel={activeView === 'yoy' ? 'Год назад' : 'Предыдущий период'} />}
                cursor={{ fill: 'rgba(102,126,234,0.06)' }}
              />
              {/* The neighbouring period is already the bar to the left, so only the year-over-year
                  view draws the compared period as a second (grey) bar. */}
              {activeView === 'yoy' && (
                <Bar dataKey="prev" fill={COLOR_PREV} name="Год назад" radius={[4, 4, 0, 0]} isAnimationActive={false} />
              )}
              <Bar dataKey="current" fill={metric === 'tax' ? COLOR_TAX : COLOR_KEPT} name={METRIC_LABEL[metric]} radius={[4, 4, 0, 0]} isAnimationActive={false}>
                {compareData.length <= LABEL_LIMIT && (
                  <LabelList
                    dataKey="deltaPct"
                    content={({ x, y, width, value }) => {
                      const pct = typeof value === 'number' ? value : null;
                      if (pct === null) return null;
                      return (
                        <text
                          x={Number(x) + Number(width) / 2}
                          y={Number(y) - 6}
                          textAnchor="middle"
                          fontSize={11}
                          fontWeight={600}
                          fill={pct < 0 ? COLOR_DOWN : COLOR_UP}
                        >
                          {formatDeltaPct(pct)}
                        </text>
                      );
                    }}
                  />
                )}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="accounting-stats-chart">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} margin={{ top: 24, right: 12, left: 8, bottom: 56 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 11 }}
                angle={groupBy === 'year' ? 0 : -32}
                textAnchor={groupBy === 'year' ? 'middle' : 'end'}
                height={52}
                interval={0}
              />
              <YAxis tick={{ fontSize: 12 }} width={64} tickFormatter={(v) => fmtInt(Number(v))} />
              <Tooltip content={<StatsTooltip />} cursor={{ fill: 'rgba(102,126,234,0.06)' }} />
              {/* recharts detects Bar children by type, so these must be direct siblings — not
                  wrapped in a Fragment. Revenue → stacked (kept-after-tax + tax); else a single bar. */}
              {metric === 'revenue' && (
                <Bar dataKey="afterTax" stackId="rev" fill={COLOR_KEPT} name="После налога" isAnimationActive={false} />
              )}
              {metric === 'revenue' && (
                <Bar dataKey="tax" stackId="rev" fill={COLOR_TAX} name="Налог 20%" radius={[4, 4, 0, 0]} isAnimationActive={false}>
                  {data.length <= LABEL_LIMIT && (
                    <LabelList dataKey="revenue" position="top" formatter={(v: number) => fmtInt(v)} style={LABEL_STYLE} />
                  )}
                </Bar>
              )}
              {metric !== 'revenue' && (
                <Bar dataKey={metric} fill={metric === 'tax' ? COLOR_TAX : COLOR_KEPT} name={METRIC_LABEL[metric]} radius={[4, 4, 0, 0]} isAnimationActive={false}>
                  {data.length <= LABEL_LIMIT && (
                    <LabelList dataKey={metric} position="top" formatter={(v: number) => fmtInt(v)} style={LABEL_STYLE} />
                  )}
                </Bar>
              )}
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

export default AccountingStats;
